import * as React from 'react';
import cx from 'classnames';
import type { Argument as ClassnamesArgument } from 'classnames';
import Portal from '@stardust/portal';
import FocusTrap from '@stardust/focus-trap';
import './style.scss';

export type PopupProps = {
  /**
   * Title to show on the top of popup box
   */
  title?: React.ReactNode | null | undefined;
  /**
   * Content of popup box
   */
  children?: React.ReactNode;
  /**
   * Custom classname for container of popup
   */
  className?: ClassnamesArgument;
  /**
   * data-cy for main button (the one on the right if there is secondary button)
   */
  mainButtonDataCy?: string;
  /**
   * Text for main button (the one on the right if there is secondary button)
   */
  mainButtonLabel?: string;
  /**
   * Overriding any styles for the main button (the one on the right if there is a secondary button)
   */
  mainButtonStyle?: Record<string | number, string & unknown>;
  /**
   * Text for secondary button (the one on the left)
   */
  secondaryButtonLabel?: string | null | undefined;
  /**
   * Overriding any styles for the secondary button
   */
  secondaryButtonStyle?: Record<string | number, string & unknown>;
  /**
   * Callback to be called when popup is dismissed
   */
  onPopupDismissed?: () => void | Promise<void>;
  /**
   * Callback to be called when main button is clicked
   */
  onClickMainButton?: (
    e?: React.MouseEvent<HTMLSpanElement>
  ) => void | Promise<void>;
  /**
   * Callback to be called when secondary button is clicked
   */
  onClickSecondaryButton?: (
    e?: React.MouseEvent<HTMLSpanElement>
  ) => void | Promise<void>;
  /**
   * Callback to be called when popup overlay is clicked
   */
  onClickOverlay?: (e?: React.MouseEvent<HTMLDivElement>) => void;
  /**
   * By default is true. Main button is hidden if this is false
   */
  displayMainButton?: boolean;
  /**
   * Specifiy custom DOM element for @stardust/portal to render this popup in
   */
  anchor?: HTMLElement;
  /**
   *
   */
  withFocusTrap?: boolean;
};

class Popup extends React.Component<PopupProps> {
  static registered: boolean;
  static defaultProps = {
    mainButtonLabel: 'OK',
    displayMainButton: true,
  };
  private mainButtonRef: React.RefObject<HTMLSpanElement> | undefined =
    React.createRef();
  private secondaryButtonRef: React.RefObject<HTMLSpanElement> | undefined =
    React.createRef();
  private uniId = Date.now();

  componentDidMount(): void {
    if (this?.secondaryButtonRef?.current) {
      this.secondaryButtonRef?.current?.focus();
    } else if (this?.mainButtonRef?.current) {
      this?.mainButtonRef?.current?.focus();
    }
  }

  componentWillUnmount() {
    typeof this.props.onPopupDismissed === 'function' &&
      this.props.onPopupDismissed();
  }

  render() {
    const {
      title,
      children,
      className,
      mainButtonDataCy,
      mainButtonLabel,
      mainButtonStyle,
      secondaryButtonLabel,
      secondaryButtonStyle,
      onClickMainButton,
      onClickSecondaryButton,
      onClickOverlay,
      displayMainButton,
      anchor,
      withFocusTrap,
    } = this.props;

    const body = (
      <div className={cx('stardust-popup', className)}>
        <div className="stardust-popup__dialog--wrapper">
          <div
            data-cy="page_popup_dialog"
            data-testid="stardust_popup_dialog"
            className="stardust-popup__dialog"
            role="dialog"
            aria-labelledby={`${className}_stardust-popup-title_${this.uniId}`}
            aria-describedby={
              title ? `${className}_stardust-popup-content_${this.uniId}` : ''
            }
            aria-modal="true"
          >
            <div className="stardust-popup__dialog--wrapper-top">
              {title && (
                <div
                  className="stardust-popup-title"
                  id={`${className}_stardust-popup-title_${this.uniId}`}
                >
                  {title}
                </div>
              )}
              <div
                className="stardust-popup-content"
                data-cy="stardust_popup_content"
                data-testid="stardust_popup_content"
                id={`${className}_stardust-popup-content_${this.uniId}`}
              >
                {children}
              </div>
            </div>
            <div className="stardust-popup-buttons">
              {secondaryButtonLabel && (
                <span
                  ref={this.secondaryButtonRef}
                  className="stardust-popup-button stardust-popup-button--secondary"
                  onClick={onClickSecondaryButton}
                  data-cy="stardust_popup_button"
                  data-testid="stardust_popup_secondary_button"
                  role="button"
                  tabIndex={0}
                  style={secondaryButtonStyle ?? {}}
                >
                  {secondaryButtonLabel}
                </span>
              )}
              {displayMainButton && (
                <span
                  ref={this.mainButtonRef}
                  data-cy={mainButtonDataCy || 'page_popup_button'}
                  data-testid="stardust_popup_main_button"
                  className="stardust-popup-button stardust-popup-button--main"
                  onClick={onClickMainButton}
                  role="button"
                  tabIndex={0}
                  style={mainButtonStyle ?? {}}
                >
                  {mainButtonLabel}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="stardust-popup__overlay" onClick={onClickOverlay} />
      </div>
    );

    return (
      <Portal anchor={anchor}>
        {withFocusTrap ? <FocusTrap element="div">{body}</FocusTrap> : body}
      </Portal>
    );
  }
}

export default Popup;
